import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { parsePhoneNumberWithError } from 'libphonenumber-js';

@customElement('product-back-in-stock-form')
class ProductBackInStockForm extends LitElement {
  @property({ type: Number, attribute: 'product-id' })
  accessor productId;

  @property({ type: Number, attribute: 'variant-id' })
  accessor variantId;

  @property({ type: String, attribute: 'attentive-zapier-url' })
  accessor attentiveZapierUrl;

  @property({ type: Number, attribute: 'source-id' })
  accessor sourceId;

  constructor() {
    super();
    this.showError = false;
    this.showSuccess = false;
    this.backInStockForm = this.querySelector('[data-back-in-stock]');
    this.backInStockError = this.querySelector('[data-back-in-stock-error]');
    this.backInStockSuccess = this.querySelector(
      '[data-back-in-stock-success]',
    );
    this.backInStockSuccessCloseButton = this.querySelector(
      '[data-back-in-stock-success-close]',
    );
    this.phoneInput = this.querySelector('[data-phone-input]');
    this.emailInput = this.querySelector('[data-email-input]');
    this.newsletterInput = this.querySelector('[data-newsletter-input]');
    this.smsInput = this.querySelector('[data-sms-input]');
  }

  connectedCallback() {
    super.connectedCallback();
    this.backInStockSuccessCloseButton.addEventListener(
      'click',
      this._resetForm,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.backInStockSuccessCloseButton.removeEventListener(
      'click',
      this._resetForm,
    );
  }

  _validatePhone = (phone) => {
    const phoneNumber = parsePhoneNumberWithError(phone, 'US');
    return phoneNumber.isValid() && phoneNumber.country === 'US';
  };

  _resetForm = () => {
    this.showError = false;
    this.showSuccess = false;
    this.backInStockError.classList.add('hidden');
    this.backInStockSuccess.classList.add('hidden');
    this.backInStockForm.classList.remove('hidden');
    this.emailInput.value = '';
    this.phoneInput.value = '';
    this.newsletterInput.checked = false;
    this.smsInput.checked = false;
  };

  _handleError = () => {
    this.showError = true;
    this.showSuccess = false;
    this.backInStockError.classList.remove('hidden');
  };

  _handleSuccess = () => {
    this.showSuccess = true;
    this.showError = false;
    this.backInStockSuccess.classList.remove('hidden');
    this.backInStockForm.classList.add('hidden');
  };

  _handleEmailSignup = ({ email, newsletter = false }) => {
    window.BIS.create(email, this.variantId, this.productId, {
      accepts_marketing: newsletter !== false,
    }).then((resp) => {
      if (resp.status === 'OK') {
        this._handleSuccess();
      } else {
        this._handleError();
      }
    });
  };

  _handlePhoneSignup = ({ email, phone, sms }) => {
    const isValid = this._validatePhone(phone);
    if (!isValid) {
      this._handleError();
      return;
    }

    const parsedPhone = parsePhoneNumberWithError(phone, 'US');
    window.BIS.create(null, this.variantId, this.productId, {
      phone_number: parsedPhone.number,
    }).then((resp) => {
      if (resp.status === 'OK') {
        this._handleSuccess();
        this._subscribeToSms({ email, phone, sms });
      } else {
        this._handleError();
      }
    });
  };

  _subscribeToSms = ({ email, phone, sms }) => {
    if (!sms || !this.sourceId) return;

    const request = {
      user: {
        email,
        phone,
      },
      signUpSourceId: this.sourceId,
      locale: {
        language: 'en',
        country: 'US',
      },
      subscriptionType: 'MARKETING',
    };

    fetch(this.attentiveZapierUrl, {
      method: 'POST',
      mode: 'cors',
      headers: {
        Accept: 'application/json',
      },
      body: JSON.stringify(request),
    })
      .then((response) => response.json())
      .then((data) => console.log(data))
      .catch((error) => console.error('Error:', error));
  };

  _handleFormSubmit = (event) => {
    event.preventDefault();
    const form = event.target;
    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());
    const { email, phone, newsletter, sms } = data;

    if (!email && !phone) {
      this._handleError();
      return;
    }

    if (email) {
      this._handleEmailSignup({ email, newsletter });
    }

    if (phone) {
      this._handlePhoneSignup({ email, phone, sms });
    }
  };

  render() {
    return html`<slot @submit=${this._handleFormSubmit}></slot>`;
  }
}

export default ProductBackInStockForm;
